import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronUpDownIcon, PlusCircleIcon } from '@heroicons/react/20/solid'
import LeadsService from '@/services/LeadsService'

const normalize = value => value?.trim().toLowerCase() || ''

export default function CampaignsCombo({
	id = 'campaign-combo',
	value = '',
	onChange,
	placeholder = 'Select or create a campaign...',
	disabled = false,
}) {
	const [query, setQuery] = useState(value || '')
	const [options, setOptions] = useState([])
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const containerRef = useRef(null)

	useEffect(() => {
		setQuery(value || '')
	}, [value])

	const fetchCampaigns = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const { data, error } = await LeadsService.getCampaigns()
			if (error) throw new Error(error)
			const cleaned = Array.isArray(data) ? data.filter(Boolean) : []
			setOptions(cleaned.sort((a, b) => a.localeCompare(b)))
		} catch (err) {
			setError(err.message || 'Unable to load campaigns')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchCampaigns()
	}, [fetchCampaigns])

	useEffect(() => {
		const handleClickOutside = e => {
			if (!containerRef.current?.contains(e.target)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const filteredOptions = useMemo(() => {
		if (!query.trim()) return options
		const normalized = normalize(query)
		return options.filter(option => normalize(option).includes(normalized))
	}, [options, query])

	const canCreateNew = useMemo(() => {
		const trimmed = query.trim()
		if (!trimmed) return false
		return !options.some(option => normalize(option) === normalize(trimmed))
	}, [options, query])

	const handleSelect = option => {
		setOpen(false)
		setQuery(option)
		onChange?.(option)
	}

	const handleCreateNew = () => {
		const trimmed = query.trim()
		if (!trimmed) return
		setOptions(prev => (prev.some(option => normalize(option) === normalize(trimmed)) ? prev : [...prev, trimmed].sort((a, b) => a.localeCompare(b))))
		setQuery(trimmed)
		onChange?.(trimmed)
		setOpen(false)
	}

	const handleKeyDown = e => {
		if (e.key === 'Enter') {
			e.preventDefault()
			if (filteredOptions.length && !canCreateNew) {
				handleSelect(filteredOptions[0])
				return
			}
			if (canCreateNew) handleCreateNew()
		}
		if (e.key === 'ArrowDown') {
			setOpen(true)
		}
	}

	return (
		<div className="relative" ref={containerRef}>
			<div className={`flex items-center rounded-lg border ${
				disabled ? 'bg-gray-100 text-gray-500' : 'bg-white'
			} border-gray-300 shadow-sm focus-within:ring-2 focus-within:ring-indigo-600`}>
				<input
					id={id}
					type="text"
					disabled={disabled}
					value={query}
					onChange={e => {
						setQuery(e.target.value)
						setOpen(true)
					}}
					onFocus={() => setOpen(true)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					className="block w-full rounded-l-lg border-0 bg-transparent py-2.5 px-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
				/>
				<button
					type="button"
					disabled={disabled}
					onClick={() => setOpen(prev => !prev)}
					className="flex h-full items-center rounded-r-lg border-l border-gray-200 px-3 text-gray-500 hover:text-gray-700 focus:outline-none">
					<ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
				</button>
			</div>

			{open && !disabled && (
				<div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
					<div className="max-h-56 overflow-y-auto py-2">
						{loading ? (
							<div className="px-4 py-2 text-sm text-gray-500">Loading campaigns...</div>
						) : filteredOptions.length ? (
							filteredOptions.map(option => (
								<button
									key={option}
									type="button"
									onMouseDown={e => e.preventDefault()}
									onClick={() => handleSelect(option)}
									className={`flex w-full items-center justify-between px-4 py-2 text-sm ${
										normalize(option) === normalize(value) ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50'
									}`}>
									<span>{option}</span>
								</button>
							))
						) : (
							<div className="px-4 py-2 text-sm text-gray-500">No campaigns found</div>
						)}
					</div>

					{canCreateNew && (
						<button
							type="button"
							onMouseDown={e => e.preventDefault()}
							onClick={handleCreateNew}
							className="flex w-full items-center gap-2 border-t border-gray-200 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50">
							<PlusCircleIcon className="h-5 w-5" aria-hidden="true" />
							Add "{query.trim()}"
						</button>
					)}
				</div>
			)}

			{error && <p className="mt-2 text-sm text-red-600">{error}</p>}
		</div>
	)
}
